import React, { useState, useMemo, useEffect } from 'react';
import { Loader2, CheckCircle2, XCircle, Trash2, Save, PlugZap, QrCode } from 'lucide-react';
import { useIntegracao, useMutateIntegracao, useConectarEvolution } from '../../../hooks/useIntegracoes';
import EvolutionQrCodeModal from './EvolutionQrCodeModal';

// ✅ Campos nunca vêm pré-preenchidos do GET (o backend mascara segredos —
// ex.: "••••1234" — e reenviar isso no PUT sobrescreveria a credencial real
// com lixo mascarado). O formulário sempre parte vazio; salvar exige
// preencher tudo de novo, igual à convenção de rotação de chave de API.
//
// `provedores` é sempre um array (mesmo canais com um único provedor, ex.:
// SMS) — o seletor de provedor só aparece quando há mais de um (WhatsApp:
// Meta Business ou Evolution API; Email: SendGrid ou SMTP Personalizado).
//
// evolution_api é um caso especial: `campos: []` (nenhum campo pedido ao
// tenant — a conexão é 100% via QR Code, não por formulário) — ver o
// branch `semFormulario` abaixo.
const STATUS_LABEL = {
    ativo: 'Configurado',
    aguardando_qrcode: 'Aguardando escaneamento',
    desconectado: 'Desconectado',
    erro: 'Erro',
    revogado: 'Desativado',
};

const IntegracaoCanalCard = ({ canal, label, icon: Icon, provedores, placeholderRemetente }) => {
    const { data, isLoading } = useIntegracao(canal);
    const { salvar, remover, testar } = useMutateIntegracao(canal);
    const conectarEvolution = useConectarEvolution();

    const [provedorSelecionado, setProvedorSelecionado] = useState(provedores[0].value);
    const provedorAtual = useMemo(
        () => provedores.find((p) => p.value === provedorSelecionado) || provedores[0],
        [provedores, provedorSelecionado]
    );

    const [form, setForm] = useState(() => provedorAtual.campos.reduce((acc, c) => ({ ...acc, [c.name]: '' }), { remetente: '' }));
    const [qrModalAberto, setQrModalAberto] = useState(false);

    // Quando a credencial já configurada carrega, o seletor parte do
    // provedor realmente ativo (não do primeiro da lista) — evita mostrar o
    // formulário errado pra quem já configurou SMTP Personalizado ou
    // Evolution API, por exemplo.
    useEffect(() => {
        if (data?.provedor && provedores.some((p) => p.value === data.provedor)) {
            setProvedorSelecionado(data.provedor);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.provedor]);

    const handleTrocarProvedor = (e) => {
        const novoProvedor = e.target.value;
        setProvedorSelecionado(novoProvedor);
        const novosCampos = provedores.find((p) => p.value === novoProvedor)?.campos || [];
        setForm(novosCampos.reduce((acc, c) => ({ ...acc, [c.name]: '' }), { remetente: '' }));
    };

    const configurado = !!data;
    const semFormulario = provedorAtual.campos.length === 0;
    const camposPreenchidos = useMemo(
        () => provedorAtual.campos.every((c) => String(form[c.name] || '').trim().length > 0),
        [form, provedorAtual]
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const montarPayload = () => {
        const payload = provedorAtual.campos.reduce((acc, c) => ({ ...acc, [c.name]: form[c.name] }), {});
        if (provedores.length > 1) payload.provedor = provedorSelecionado;
        if (form.remetente) payload.remetente = form.remetente;
        return payload;
    };

    const handleSalvar = (e) => {
        e.preventDefault();
        if (!camposPreenchidos) return;
        salvar.mutate(montarPayload(), {
            onSuccess: () => setForm(provedorAtual.campos.reduce((acc, c) => ({ ...acc, [c.name]: '' }), { remetente: '' })),
        });
    };

    const handleTestar = () => {
        if (semFormulario) {
            testar.mutate({});
        } else if (camposPreenchidos) {
            testar.mutate(montarPayload());
        } else {
            testar.mutate({});
        }
    };

    const handleRemover = () => {
        if (window.confirm(`Desativar a credencial de ${label}? O envio por este canal para de funcionar.`)) {
            remover.mutate();
        }
    };

    const handleConectarEvolution = () => {
        conectarEvolution.mutate(undefined, {
            onSuccess: () => setQrModalAberto(true),
        });
    };

    const podeTestar = semFormulario ? configurado : (camposPreenchidos || configurado);
    const statusLabel = configurado ? (STATUS_LABEL[data.status] || data.status) : null;
    const statusOk = data?.status === 'ativo';

    return (
        <form onSubmit={handleSalvar}>
            {qrModalAberto && (
                <EvolutionQrCodeModal
                    qrCodeInicial={conectarEvolution.data?.qrCode}
                    onClose={() => setQrModalAberto(false)}
                />
            )}
            <div className="content-panel" style={{ marginBottom: '1rem' }}>
                <div className="form-section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <h4 className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                            <Icon size={18} className="icon" />
                            {label}
                            {provedores.length === 1 && (
                                <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                    ({provedores[0].label})
                                </span>
                            )}
                        </h4>
                        {isLoading ? (
                            <Loader2 size={16} className="icon animate-spin" />
                        ) : configurado ? (
                            <span className={`badge ${statusOk ? 'badge-green' : 'badge-red'}`}>
                                {statusLabel}
                            </span>
                        ) : (
                            <span className="badge badge-yellow">Não configurado</span>
                        )}
                    </div>

                    {configurado && !semFormulario && (
                        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                            Remetente atual: <strong>{data.remetente || '—'}</strong>
                            {' · '}
                            {Object.entries(data.campos || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </p>
                    )}

                    {provedores.length > 1 && (
                        <div className="form-group" style={{ marginTop: '0.75rem', maxWidth: '28rem' }}>
                            <label className="form-label">Provedor</label>
                            <select
                                className="form-input"
                                value={provedorSelecionado}
                                onChange={handleTrocarProvedor}
                            >
                                {provedores.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {semFormulario ? (
                        <div style={{ marginTop: '0.75rem' }}>
                            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginBottom: '0.75rem' }}>
                                Conecte o número de WhatsApp do seu celular escaneando um QR Code — sem precisar de aprovação da Meta.
                            </p>
                            <button
                                type="button"
                                className="action-button action-button-primary"
                                onClick={handleConectarEvolution}
                                disabled={conectarEvolution.isPending}
                            >
                                {conectarEvolution.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <QrCode size={18} className="icon" />}
                                {configurado ? 'Reconectar via QR Code' : 'Conectar via QR Code'}
                            </button>
                        </div>
                    ) : (
                        <div className="form-grid-container-cols-2" style={{ marginTop: '0.75rem' }}>
                            {provedorAtual.campos.map((c) => (
                                <div className="form-group" key={c.name}>
                                    <label className="form-label required">{c.label}</label>
                                    <input
                                        name={c.name}
                                        type={c.type || 'text'}
                                        value={form[c.name] || ''}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder={configurado ? 'Digite para substituir o valor salvo' : (c.placeholder || c.label)}
                                        autoComplete="off"
                                    />
                                </div>
                            ))}
                            <div className="form-group">
                                <label className="form-label">Remetente (opcional)</label>
                                <input
                                    name="remetente"
                                    value={form.remetente}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder={placeholderRemetente}
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    )}

                    {testar.data && (
                        <div
                            className={`alert ${testar.data.valido ? 'alert-success' : 'alert-danger'}`}
                            style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            {testar.data.valido ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                            {testar.data.valido
                                ? `Credencial válida (origem: ${testar.data.origem === 'campos_informados' ? 'valores digitados' : 'credencial salva'})`
                                : (testar.data.erro || 'Credencial inválida')}
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    {configurado && (
                        <button
                            type="button"
                            className="action-button action-button-secondary"
                            onClick={handleRemover}
                            disabled={remover.isPending}
                        >
                            {remover.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <Trash2 size={18} className="icon" />}
                            Desativar
                        </button>
                    )}
                    <button
                        type="button"
                        className="action-button action-button-secondary"
                        onClick={handleTestar}
                        disabled={!podeTestar || testar.isPending}
                    >
                        {testar.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <PlugZap size={18} className="icon" />}
                        Testar
                    </button>
                    {!semFormulario && (
                        <button
                            type="submit"
                            className="action-button action-button-primary"
                            disabled={!camposPreenchidos || salvar.isPending}
                        >
                            {salvar.isPending ? <Loader2 size={18} className="icon animate-spin" /> : <Save size={18} className="icon" />}
                            Salvar
                        </button>
                    )}
                </div>
            </div>
        </form>
    );
};

export default IntegracaoCanalCard;
